import type { SizeOptionWithEdd } from '@/features/customization/domain/models/CustomizationOptions';
import type { SizeOption } from '@/features/customization/utils/parseSizeList';
import type { PdpEddProductSpecs } from '../domain/models/ResolvedExpectedDeliveryDate';
import {
  formatEddDate,
  getCustomizationEddKeyFromSpecs,
  isSizeableSkuMap,
} from './eddFormatting';

function parseSkuMapToSizeList(skuMap: Record<string, Record<string, unknown>>): SizeOptionWithEdd[] {
  const sizeList: SizeOptionWithEdd[] = [];

  Object.keys(skuMap)
    .sort()
    .forEach((key) => {
      const entry = skuMap[key];
      const sizeKey = String(entry.size ?? key);
      const displaySize = String(entry.displaySize ?? entry.size ?? key);
      const deliveryDateInfo = entry.deliveryDateInfo as Record<string, unknown> | undefined;
      const expectedDeliveryDate =
        typeof deliveryDateInfo?.expectedDeliveryDate === 'string'
          ? deliveryDateInfo.expectedDeliveryDate
          : null;

      sizeList.push({
        key: sizeKey,
        value: displaySize,
        edd: expectedDeliveryDate ? formatEddDate(expectedDeliveryDate) : null,
        eddFullDate: expectedDeliveryDate,
      });
    });

  return sizeList;
}

export function parseEddSizeList(
  designEddDetails: unknown | null,
  productSpecs: PdpEddProductSpecs,
  fallbackSizes: SizeOption[]
): SizeOptionWithEdd[] {
  if (!designEddDetails || typeof designEddDetails !== 'object') {
    return fallbackSizes.map((size) => ({ ...size }));
  }

  const eddData = designEddDetails as Record<string, unknown>;
  const key = getCustomizationEddKeyFromSpecs(productSpecs);
  const customization = eddData[key] as Record<string, unknown> | undefined;

  if (!customization?.skuCodeMap) {
    return fallbackSizes.map((size) => ({ ...size }));
  }

  const skuMap = customization.skuCodeMap as Record<string, Record<string, unknown>>;
  if (!isSizeableSkuMap(skuMap)) {
    return fallbackSizes.map((size) => ({ ...size }));
  }

  const eddSizes = parseSkuMapToSizeList(skuMap);
  if (eddSizes.length === 0) {
    return fallbackSizes.map((size) => ({ ...size }));
  }

  return eddSizes;
}
